import React, { useState, useEffect, useCallback } from 'react';
import api, { extractApiError } from '../../services/api';
import toast from 'react-hot-toast';
import UserModal from '../../components/admin/UserModal';
import ConfirmModal from '../../components/common/ConfirmModal';

const STATUS_LABELS = {
  active:  { label: 'Ativo',      color: '#16a34a', bg: '#f0fdf4' },
  blocked: { label: 'Bloqueado',  color: '#dc2626', bg: '#fef2f2' },
  inactive:{ label: 'Inativo',    color: '#6b7280', bg: '#f9fafb' },
  pending: { label: 'Pendente',   color: '#d97706', bg: '#fffbeb' },
};

const ROLE_LABELS = {
  admin:   { label: 'Admin',   color: '#7c3aed', bg: '#f5f3ff' },
  lojista: { label: 'Lojista', color: '#1e40af', bg: '#eff6ff' },
};

export default function UsersPage() {
  const [users, setUsers]               = useState([]);
  const [meta, setMeta]                 = useState({ total: 0, page: 1, limit: 20, pages: 1 });
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter]     = useState('');
  const [modal, setModal]               = useState(null);
  const [confirm, setConfirm]           = useState(null);
  const [page, setPage]                 = useState(1);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search)       params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (roleFilter)   params.role   = roleFilter;

      const { data } = await api.get('/admin/users', { params });
      // envelope: { success, data: { users: [], meta: {} }, message }
      setUsers(data.data.users);
      setMeta(data.data.meta);
    } catch {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, roleFilter]);

  useEffect(() => {
    const t = setTimeout(fetchUsers, search ? 400 : 0);
    return () => clearTimeout(t);
  }, [fetchUsers, search]);

  async function handleSave(userData, editingId) {
    try {
      if (editingId) {
        await api.put(`/admin/users/${editingId}`, userData);
        toast.success('Usuário atualizado com sucesso');
      } else {
        await api.post('/admin/users', userData);
        toast.success('Usuário criado com sucesso');
      }
      setModal(null);
      fetchUsers();
    } catch (err) {
      toast.error(extractApiError(err, 'Erro ao salvar usuário'));
    }
  }

  async function handleDelete(userId) {
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('Usuário removido com sucesso');
      setConfirm(null);
      fetchUsers();
    } catch (err) {
      toast.error(extractApiError(err, 'Erro ao remover usuário'));
    }
  }

  async function handleToggleStatus(user) {
    try {
      const { data } = await api.patch(`/admin/users/${user.id}/toggle-status`);
      // envelope: { success, data: { status }, message }
      toast.success(data.message);
      fetchUsers();
    } catch (err) {
      toast.error(extractApiError(err, 'Erro ao alterar status'));
    }
  }

  const statusInfo = (s) => STATUS_LABELS[s] || STATUS_LABELS.inactive;
  const roleInfo   = (r) => ROLE_LABELS[r]   || ROLE_LABELS.lojista;

  return (
    <div>
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>Usuários</h1>
          <p style={styles.pageDesc}>Gerencie os usuários da plataforma</p>
        </div>
        <button style={styles.btnPrimary} onClick={() => setModal({ type: 'create' })}>
          + Novo Usuário
        </button>
      </div>

      <div style={styles.filters}>
        <input
          style={styles.searchInput}
          placeholder="Buscar por nome, e-mail ou empresa..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        <select style={styles.select} value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">Todos os status</option>
          <option value="active">Ativo</option>
          <option value="blocked">Bloqueado</option>
          <option value="pending">Pendente</option>
          <option value="inactive">Inativo</option>
        </select>
        <select style={styles.select} value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}>
          <option value="">Todos os perfis</option>
          <option value="admin">Admin</option>
          <option value="lojista">Lojista</option>
        </select>
      </div>

      <div style={styles.tableCard}>
        <div style={styles.tableHeader}>
          <span style={styles.tableCount}>{meta.total} usuário{meta.total !== 1 ? 's' : ''}</span>
        </div>

        {loading ? (
          <div style={styles.emptyState}>Carregando...</div>
        ) : users.length === 0 ? (
          <div style={styles.emptyState}>Nenhum usuário encontrado</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Nome', 'E-mail', 'Empresa', 'Perfil', 'Status', 'Cadastro', 'Ações'].map((h) => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} style={styles.tr}>
                    <td style={styles.td}>
                      <div style={styles.nameCell}>
                        <div style={styles.avatar}>{u.name?.charAt(0).toUpperCase()}</div>
                        <span style={styles.userName}>{u.name}</span>
                      </div>
                    </td>
                    <td style={styles.td}><span style={styles.email}>{u.email}</span></td>
                    <td style={styles.td}>{u.companyName || <span style={styles.empty}>—</span>}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, color: roleInfo(u.role).color, background: roleInfo(u.role).bg }}>
                        {roleInfo(u.role).label}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, color: statusInfo(u.status).color, background: statusInfo(u.status).bg }}>
                        {statusInfo(u.status).label}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.date}>
                        {new Date(u.createdAt).toLocaleDateString('pt-BR')}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actions}>
                        <button style={styles.btnAction} onClick={() => setModal({ type: 'edit', user: u })} title="Editar">✏️</button>
                        <button
                          style={styles.btnAction}
                          onClick={() => handleToggleStatus(u)}
                          title={u.status === 'blocked' ? 'Desbloquear' : 'Bloquear'}
                        >{u.status === 'blocked' ? '🔓' : '🔒'}</button>
                        <button
                          style={{ ...styles.btnAction, ...styles.btnDanger }}
                          onClick={() => setConfirm({ userId: u.id, name: u.name })}
                          title="Excluir"
                        >🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {meta.pages > 1 && (
          <div style={styles.pagination}>
            <button style={styles.pageBtn} disabled={page <= 1} onClick={() => setPage(page - 1)}>← Anterior</button>
            <span style={styles.pageInfo}>Página {page} de {meta.pages}</span>
            <button style={styles.pageBtn} disabled={page >= meta.pages} onClick={() => setPage(page + 1)}>Próximo →</button>
          </div>
        )}
      </div>

      {modal && (
        <UserModal user={modal.user} onSave={handleSave} onClose={() => setModal(null)} />
      )}

      {confirm && (
        <ConfirmModal
          title="Confirmar exclusão"
          message={`Tem certeza que deseja remover o usuário "${confirm.name}"? Esta ação não pode ser desfeita.`}
          onConfirm={() => handleDelete(confirm.userId)}
          onCancel={() => setConfirm(null)}
          confirmLabel="Remover"
          danger
        />
      )}
    </div>
  );
}

const styles = {
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  pageTitle: { fontSize: '24px', fontWeight: '700', color: '#111827' },
  pageDesc: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },
  btnPrimary: {
    background: 'linear-gradient(135deg, #1e40af, #2563eb)',
    color: '#fff', border: 'none', borderRadius: '8px',
    padding: '10px 18px', fontSize: '14px', fontWeight: '600',
    cursor: 'pointer', whiteSpace: 'nowrap',
  },
  filters: { display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' },
  searchInput: {
    flex: 1, minWidth: '200px', padding: '9px 14px',
    border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', outline: 'none',
  },
  select: {
    padding: '9px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px',
    fontSize: '14px', background: '#fff', cursor: 'pointer', outline: 'none',
  },
  tableCard: {
    background: '#fff', borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f3f4f6', overflow: 'hidden',
  },
  tableHeader: { padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center' },
  tableCount: { fontSize: '13px', fontWeight: '600', color: '#6b7280' },
  emptyState: { padding: '48px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: {
    padding: '11px 16px', textAlign: 'left', fontSize: '12px',
    fontWeight: '600', color: '#6b7280', textTransform: 'uppercase',
    letterSpacing: '0.5px', background: '#f9fafb',
    borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap',
  },
  tr: { borderBottom: '1px solid #f3f4f6', transition: 'background 0.1s' },
  td: { padding: '13px 16px', color: '#374151', verticalAlign: 'middle' },
  nameCell: { display: 'flex', alignItems: 'center', gap: '10px' },
  avatar: {
    width: '32px', height: '32px',
    background: 'linear-gradient(135deg, #1e40af, #3b82f6)',
    borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '13px', fontWeight: '700', color: '#fff', flexShrink: 0,
  },
  userName: { fontWeight: '500', color: '#111827' },
  email: { color: '#6b7280', fontSize: '13px' },
  empty: { color: '#d1d5db' },
  badge: { display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
  date: { color: '#9ca3af', fontSize: '13px' },
  actions: { display: 'flex', gap: '4px' },
  btnAction: {
    background: 'none', border: '1px solid #e5e7eb', borderRadius: '6px',
    padding: '5px 8px', cursor: 'pointer', fontSize: '14px', transition: 'background 0.1s',
  },
  btnDanger: { borderColor: '#fecaca' },
  pagination: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '16px', padding: '16px', borderTop: '1px solid #f3f4f6',
  },
  pageBtn: {
    padding: '7px 14px', border: '1px solid #e5e7eb', borderRadius: '6px',
    background: '#fff', cursor: 'pointer', fontSize: '13px', color: '#374151', fontFamily: 'inherit',
  },
  pageInfo: { fontSize: '13px', color: '#6b7280' },
};
